<template>
  <div id="myChatter">
    <div class="chat-popup" id="myForm" :class="{ 'dark-theme-container': isDarkTheme }">
      <header class="header" :class="{ 'dark-theme': isDarkTheme }">
        <div class="header-left">
          <img :src="headerImage" alt="" class="header-logo">
          <h1 style="width: 100%; margin: 0; line-height: 1.2;" v-dompurify-html="name"></h1>
        </div>
        <div class="header-right">
          <div class="theme-switch-wrapper">
            <v-tooltip location="bottom" :text="$t('message.language')">
              <template v-slot:activator="{ props }">
                <div
                  v-bind="props"
                  class="language-selector circular-selector"
                  role="group"
                  :aria-label="currentLocale === 'en' ? 'Language selection' : 'Selección de idioma'"
                >

                  <v-select
                    v-model="currentLocale"
                    :items="availableLocales"
                    item-title="name"
                    item-value="code"
                    density="compact"
                    variant="outlined"
                    hide-details
                    tabindex="0"
                    aria-labelledby="language-selector-label"
                    @update:model-value="changeLanguage"
                  >
                  <template v-slot:prepend>
                    <label id="language-selector-label" class="sr-only">
                      {{ currentLocale === 'en' ? 'Current language: English' : 'Selector de idioma. Idioma actual: Español' }}
                    </label>
                  </template>
                  </v-select>
                </div>
              </template>
            </v-tooltip>
            <v-tooltip location="bottom">
              <template v-slot:activator="{ props }">
                <button
                  v-bind="props"
                  class="theme-button"
                  @click="toggleTheme"
                  :aria-label="`Switch to ${isDarkTheme ? 'light' : 'dark'} theme`"
                >
                  <Moon v-if="!isDarkTheme" class="theme-icon" />
                  <Sun v-else class="theme-icon" />
                </button>
              </template>
              <span>{{ isDarkTheme ? $t('message.light') : $t('message.dark') }}</span>
            </v-tooltip>
            <div id="switch-0-messages" class="sr-only" aria-hidden="true">
              Toggle between light and dark theme
            </div>
          </div>

          <v-tooltip location="bottom" :text="isExpanded ? $t('message.minimize') : $t('message.maximize')">
            <template v-slot:activator="{ props }">
              <button
                v-if="showExpansionButton"
                v-bind="props"
                class="expand-button"
                @click="resizeChat($event)"
                @keydown.enter.prevent="resizeChat($event)"
                @keydown.space.prevent="resizeChat($event)"
                :aria-label="isExpanded ? 'Collapse chat window' : 'Expand chat window'"
                :aria-expanded="isExpanded"
                tabindex="0">
                <Maximize2 v-if="!isExpanded || showMaximizeIcon" class="expand-icon" />
                <Minimize2 v-else class="expand-icon" />
              </button>
            </template>
          </v-tooltip>

          <!-- <v-tooltip location="bottom" text="Close">
            <template v-slot:activator="{ props }">
              <button
                v-bind="props"
                class="close-button"
                @click="closeChat"
                @keydown.enter.prevent="closeChat"
                @keydown.space.prevent="closeChat"
                aria-label="Close chat window"
                tabindex="0">
                <X class="close-icon" />
              </button>
            </template>
            <span>{{ $t('message.close') }}</span>
          </v-tooltip> -->
        </div>
      </header>

      <ChatConnection v-if="!streaming" ref="chat" @name-updated="handleNameUpdate" @disclaimer-updated="handleDisclaimerUpdate" :is-expanded="isExpanded"/>
      <ChatConnectionStreaming v-if="streaming" ref="chat" @name-updated="handleNameUpdate" @disclaimer-updated="handleDisclaimerUpdate" :is-expanded="isExpanded"/>

      <footer class="disclaimer" :class="{ 'dark-theme': isDarkTheme }">
        {{ disclaimerText }}
      </footer>
    </div>
  </div>
</template>
<script>
import { Sun, Moon, Maximize2, Minimize2, X } from 'lucide-vue-next';
import ChatConnection from "@/components/ChatConnection";
import ChatConnectionStreaming from "@/components/ChatConnectionStreaming.vue";
import coedImage from "@/assets/images/coed.png";
import sealImg from "@/assets/images/seal.png";
import { useTheme } from 'vuetify'
import { provide, computed } from 'vue'
import darkModeIcon from '../assets/images/dark-mode.png';
import lightModeIcon from '../assets/images/light-mode.png';


export default {
  props: {
    theme: Object,
    streaming: String
  },
  components: {ChatConnectionStreaming, ChatConnection, Sun, Moon, Maximize2, Minimize2, X},
  setup() {
    const theme = useTheme()

    provide('theme', theme)

    const isDarkTheme = computed(() => theme.global.current.value.dark);

    const toggleTheme = () => {
      // We're going to use a custom class approach rather than changing the global theme
      theme.global.name.value = theme.global.current.value.dark ? 'myCustomLightTheme' : 'myCustomDarkTheme'
    }

    return { theme, toggleTheme, isDarkTheme }
  },

  data() {
    return {
      menuVisible: false,
      isExpanded: true,
      previousExpanded: true,
      name: "",
      disclaimerText: "I can make mistakes. Consider checking important information.",
      darkModeIcon,
      lightModeIcon,
      currentLocale: this.$i18n.locale,
      availableLocales: [
        { name: 'EN', code: 'en' },
        { name: 'ES', code: 'es' }
      ],
      isInIframe: false
    };
  },
  computed: {
  showExpansionButton() {
    const isLargeWindow = this.$route.query.size === 'large';
    const isExpansionDisabled = this.$route.query.disableExpand === 'true';
    return !isLargeWindow && !isExpansionDisabled;
    // return !isLargeWindow;
  },
  showMaximizeIcon() {
    console.log("is in iframe? ", this.isInIframe)
    return this.isInIframe || !this.isExpanded;
  },
  headerImage() {
    const botName = (this.$route?.query?.botName || "").toLowerCase();
      if (botName === "coed") {
        return coedImage
      } else {
        return sealImg
      }
    }
  },
  methods: {
    toggleMenu() {
      this.menuVisible = !this.menuVisible;
    },
    handleNameUpdate(newName) {
      console.log('Received name from ChatConnection:', newName); // Debugging log
      this.name = newName;
    },
    handleDisclaimerUpdate(newDisclaimer) {
      console.log('Received disclaimer from ChatConnection:', newDisclaimer); // Debugging log
      this.disclaimerText = newDisclaimer;
    },
    resizeChat(event) {
      // Handle both click and keyboard events
      if (event && event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      let chatForm = document.getElementById("myForm");
      if (this.isExpanded) {
        chatForm.style.width = "320px";
        chatForm.style.height = "470px";
      } else {
        chatForm.style.width = "calc(100vw - 8px)";
        chatForm.style.height = "calc(100vh - 8px)";
      }
      this.isExpanded = !this.isExpanded;

      // Announce state change for screen readers
      const message = this.isExpanded ? 'Chat window expanded' : 'Chat window collapsed';
      this.$nextTick(() => {
        // Create a temporary announcement element
        const announcement = document.createElement('div');
        announcement.setAttribute('role', 'status');
        announcement.setAttribute('aria-live', 'polite');
        announcement.className = 'sr-only';
        announcement.textContent = message;
        document.body.appendChild(announcement);

        // Remove it after announcement
        setTimeout(() => {
          document.body.removeChild(announcement);
        }, 1000);
      });
    },

    changeLanguage(locale) {
      this.$i18n.locale = locale;
      const botName = this.$route.query.botName;
      this.$refs.chat.fetchGreeting(botName);
    },
    closeChat() {
      this.$emit('close');
    },
  },
  mounted() {

    try {
      this.isInIframe = window.self !== window.top;
    } catch (e) {
      // If we get a security error, we're definitely in an iframe
      // with a different origin
      this.isInIframe = true;
    }
    if(this.$route.query.size == "large"){
      document.getElementById("myForm").style.margin = "10px";
      document.getElementById("myForm").style.position = "static";
    }
    else {
      document.getElementById("myForm").style.height = "calc(100vh - 8px)";
      document.getElementById("myForm").style.width = "calc(100vw - 8px)";
    }
  },
}
</script>

<style scoped>

#myChatter {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 320px;
  overflow: auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 8px;
  font-size: 10px;
  height: 47px;
  width: 100%;
  background-color: var(--header-bg, #e21833);
  color: var(--header-text, #FFFFFF);
  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
  font-family: Ginto, ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", Segoe UI Symbol, "Noto Color Emoji";
}

:root {
  --header-bg: #e21833;
  --header-text: #FFFFFF;
}

[data-theme="dark"] {
  --header-bg: #2F3033;
  --header-text: #FFFFFF;
}

.header-dark {
  background-color: #2F3033;
  color: #FFFFFF;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-right {
  display: flex;
  align-items: center;
}

.header-logo {
  width: 38px;
  height: 38px;
  min-width: 38px;
  object-fit: contain;
}

.header-logo {
  width: 38px;
  height: 38px;
  min-width: 38px;
  object-fit: contain;
}

.themeToggle {
  transform: scale(0.8);
  min-width: 40px;
}

.theme-icon {
  width: 20px;
  height: 20px;
  min-width: 20px;
}

.chat-popup {
  position: fixed;
  bottom: 5px;
  right: 5px;
  border-radius: 8px;
  min-width: 320px;
  max-width: 100vw;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
  transform-origin: bottom right;
}

.disclaimer {
  color: #454545;
  background-color: #e6e6e6;
  font-size: 13px;
  text-align: center;
}
.menu {
  display: none;
  position: absolute;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 8px;
}

.menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.menu li {
  padding: 5px 0;
}

.menu_button {
  font-size: 18px;
  padding-right: 20px;
}

#expand {
  font-size: 18px;
  padding-right: 10px;
}

.theme-button,
.expand-button,
.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  transition: background-color 0.3s, transform 0.2s;
}

.language-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
  position: relative;
  width: 36px; /* Match the height for a perfect circle */
  height: 36px;
}

/* Use a pseudo-element for the hover effect */
.language-selector::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: transparent;
  transition: background-color 0.3s;
  z-index: 0;
  pointer-events: none;
}

.language-selector:hover::before {
  background-color: rgba(255, 255, 255, 0.2);
}

.language-selector :deep(.v-field.v-field--focused) {
  /* Add a visible white outline */
  outline: 2px solid white !important;
  outline-offset: 2px !important;
  border-radius: 50% !important;
  /* Add a slight background change like your hover effect */
  background-color: rgba(255, 255, 255, 0.2) !important;
}

/* For dark theme */
.dark-theme .language-selector :deep(.v-field.v-field--focused) {
  outline: 2px solid white !important;
  background-color: rgba(255, 255, 255, 0.15) !important;
}

/* Ensure the field maintains its circular shape */
.language-selector :deep(.v-field) {
  transition: outline 0.2s, background-color 0.2s;
}

.circular-selector {
  position: relative;
  z-index: 1;
  width: 42px;
  height: 42px;
}

.circular-selector :deep(.v-field) {
  border-radius: 50% !important;
  width: 36px !important;
  height: 36px !important;
  min-height: 36px !important;
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circular-selector :deep(.v-field__input) {
  padding: 0 !important;
  text-align: center;
  min-height: 0 !important;
  color: white
}

.circular-selector :deep(.v-field__append-inner) {
  padding-inline-start: 0 !important;
  margin-left: -14px !important;
  margin-right: -4px !important;
}

.circular-selector :deep(.v-field__field) {
  padding-inline: 8px !important;
}

/* .language-selector:hover, */
.theme-button:hover,
.expand-button:hover,
.close-button:hover {
  background-color: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.circular-selector:hover {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  transform: scale(1.05);
}

.language-selector:hover,
.dark-theme .theme-button:hover,
.dark-theme .expand-button:hover,
.dark-theme .close-button:hover {
  background-color: rgba(255, 255, 255, 0.15);
}

/* Container styling for the chat */
#myForm {
  /*box-shadow: 0 0 5px 2.5px #e21833;*/
  transition: width 0.3s ease-in-out, height 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
}

/* Dark theme container */
.dark-theme-container {
  box-shadow: 0 0 5px 2.5px rgba(255, 107, 107, 0.5);
}

#fullBox, #messages {
  transition: width 0.3s ease-in-out, height 0.3s ease-in-out;
}

.expand-button {
  margin-right: 10px;
}

.theme-button,
.expand-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  transition: background-color 0.3s, transform 0.2s;
}

.theme-button:hover,
.expand-button:hover {
  background-color: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.dark-theme .theme-button:hover,
.dark-theme .expand-button:hover  {
  background-color: rgba(255, 255, 255, 0.15);
}


/* Default style for larger screens */
.header-right .themeToggle {
  margin-right: 1vw;
}

.header-right img {
  margin-right: 1vw;
}

.disclaimer.dark-theme {
  color: #E0E0E0;
  background-color: #000000 !important;
}

.theme-switch-wrapper {
  display: flex;
  align-items: center;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
